<script setup lang="ts">
import { computed, onMounted } from 'vue';
import useAuth from './composables/useAuth';
import useUser from './composables/useUser';
import BaseSpinner from './components/BaseSpinner.vue';
import BaseToast from './components/BaseToast.vue';
import BaseConfirmDialog from './components/BaseConfirmDialog.vue';
import BaseMediaDialog from './components/BaseMediaDialog.vue';
import TheVersionGate from './components/TheVersionGate.vue';
import { useAuthStore } from './stores/useAuthStore';
import { storeToRefs } from 'pinia';
import { useRoute, type RouteMeta } from 'vue-router';
import { defineAsyncComponent } from 'vue';

const route = useRoute();
// @ts-expect-error any layout vue is valid
const layout: Record<RouteMeta['layout'] | 'div', () => Promise<typeof import('*.vue')>> = {
  dashboard: () => import('./layouts/dashboard.vue'),
  auth: () => import('./layouts/auth.vue'),
};
const layoutName = computed(() => {
  return route.meta?.layout ?? 'div';
});
const LayoutComponent = computed(() => {
  const loader = layout[layoutName.value];
  return loader ? defineAsyncComponent(loader) : 'div';
});

const { setCsrfToken } = useAuth();
const { fetchMe } = useUser();

const authStore = useAuthStore();
const { initialized } = storeToRefs(authStore);
initialized.value = import.meta.env.VITE_STATIC === 'true';

onMounted(async () => {
  try {
    await fetchMe();
    await setCsrfToken();
  } finally {
    initialized.value = true;
  }
});
</script>

<template>
  <TheVersionGate />
  <div v-if="!initialized" class="min-h-screen bg-sand-50 flex items-center justify-center">
    <BaseSpinner />
  </div>
  <div v-else>
    <BaseToast />
    <BaseConfirmDialog />
    <BaseMediaDialog />
    <component :is="LayoutComponent">
      <RouterView />
    </component>
  </div>
</template>
